import React, { useEffect } from 'react';
import { Card } from '@material-ui/core';
import { Policy as PolicyIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineRadioGroup, {
  InlineRadioGroupProps
} from '../../../common/inline/InlineRadioGroup';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import { BaseSupplierCardProps } from '../Supplier.interface';

const SupplierQualityProgramCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Supplier>(
    supplier.task,
    { token }
  );
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;
  const commonOptions: InlineRadioGroupProps['options'] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'N/A', value: 'n/a' }
  ];

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <PolicyIcon />
        Quality Program
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          isSubmitting={isUpdating}
          initialValues={{
            hasQualityProgram: initialData.hasQualityProgram || '',
            qualityProgramBasedOn: initialData.qualityProgramBasedOn || '',
            seperateFromProductionDepartment:
              initialData.seperateFromProductionDepartment || '',
            seperateFromProductionDepartmentDescription:
              initialData.seperateFromProductionDepartmentDescription || '',
            hasCurrentQualityManual: initialData.hasCurrentQualityManual || ''
          }}
          onSubmit={(supplierDto: Partial<any>) => {
            updateSupplier(supplierDto);
            onChange?.(supplierDto);
          }}
        >
          {() => (
            <InlineForm>
              <InlineRadioGroup
                name='hasQualityProgram'
                label='Is there an established Quality Program?'
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasQualityProgram')}
              />
              <InlineTextField
                name='qualityProgramBasedOn'
                label='What system is the Quality Program based on?'
                placeholder='Enter answer'
                disabled={disabled || isFieldDisabled('qualityProgramBasedOn')}
              />
              <InlineRadioGroup
                name='seperateFromProductionDepartment'
                label='Is the Quality Control/Inspection/Assurance organization separate from the Production Department?'
                options={commonOptions}
                disabled={
                  disabled ||
                  isFieldDisabled('seperateFromProductionDepartment')
                }
              />
              <InlineTextField
                name='seperateFromProductionDepartmentDescription'
                label="If you answered 'NO' to the question above, explain why:"
                placeholder='Enter explanation'
                disabled={
                  disabled ||
                  isFieldDisabled('seperateFromProductionDepartmentDescription')
                }
              />
              <InlineRadioGroup
                name='hasCurrentQualityManual'
                label='Is there an up-to-date Quality Manual?'
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasCurrentQualityManual')
                }
              />
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierQualityProgramCard;
